import React, { useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useNonWorkingStats } from '../hooks/useNonWorkingStats';
import GuardSelect from '@/pages/scheduling/components/GuardSelect';
import { Users, ClipboardX, Stethoscope, CalendarX, Ban, Save } from 'lucide-react';
import { useUserRoles } from '@/hooks/useUserRoles';
import { useToast } from '@/hooks/use-toast';
import YearSelect from './YearSelect';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { format, parseISO } from 'date-fns';

const StatBox: React.FC<{ title: string; value: number; ytdValue: number; base?: number; delta?: number; icon?: React.ReactNode; onClick?: () => void }> = ({ title, value, ytdValue, base, delta, icon, onClick }) => (
  <Card
    role="button"
    tabIndex={0}
    onClick={onClick}
    className="p-5 cursor-pointer hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/50"
  >
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-sm font-medium text-foreground/80">{title}</h3>
      {icon}
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <div className="text-xs text-muted-foreground">Μέχρι σήμερα</div>
        <div className="text-2xl font-bold text-foreground">{ytdValue}</div>
      </div>
      <div>
        <div className="text-xs text-muted-foreground">Έτος</div>
        <div className="text-2xl font-bold text-foreground">{value}</div>
      </div>
    </div>
    {typeof base === 'number' && typeof delta === 'number' && (
      <div className="text-xs text-muted-foreground mt-2">calc {base} + adj {delta}</div>
    )}
  </Card>
);

const EmployeeNonWorkingStats: React.FC = () => {
  const { isAdmin } = useUserRoles();
  const { toast } = useToast();
  const [selectedGuardId, setSelectedGuardId] = useState<string | undefined>(undefined);
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<'regular' | 'previousYear' | 'sick' | 'noWork' | null>(null);
  const openDetails = (t: 'regular' | 'previousYear' | 'sick' | 'noWork') => { setSelectedType(t); setDetailOpen(true); };

  // Load guards for admin dropdown
  const guardsQuery = useQuery({
    queryKey: ['guards-basic-list'],
    enabled: !!isAdmin,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, first_name, last_name')
        .order('full_name', { ascending: true });
      if (error) throw error;
      return data || [];
    },
  });

  const { effectiveGuardId, statsQuery, saveMutation } = useNonWorkingStats({ selectedGuardId, year, isAdmin });

  const [form, setForm] = useState({
    regular: 0,
    previousYear: 0,
    sick: 0,
    noWork: 0,
    notes: '',
  });

  React.useEffect(() => {
    if (statsQuery.data?.adjustments) {
      setForm({
        regular: statsQuery.data.adjustments.regular,
        previousYear: statsQuery.data.adjustments.previousYear,
        sick: statsQuery.data.adjustments.sick,
        noWork: statsQuery.data.adjustments.noWork,
        notes: statsQuery.data.adjustments.notes || '',
      });
    }
  }, [statsQuery.data?.adjustments]);

  const guards = guardsQuery.data || [];
  const selectedGuardName = useMemo(() => guards.find(g => g.id === selectedGuardId)?.full_name, [guards, selectedGuardId]);

  const onSave = async () => {
    try {
      await saveMutation.mutateAsync(form);
      toast({ title: 'Saved', description: 'Adjustments updated successfully.' });
    } catch (e: any) {
      toast({ title: 'Save failed', description: e.message || 'Could not save adjustments', variant: 'destructive' });
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Users className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold">Non-working shifts ({year})</h2>
            <p className="text-muted-foreground">Regular, previous year, sick leave and no-work days for the selected employee in {year}.</p>
          </div>
        </div>
        {isAdmin && (
          <div className="w-48">
            <Label htmlFor="year">Έτος</Label>
            <YearSelect year={year} onChange={(y) => setYear(y)} />
          </div>
        )}
      </div>

      {/* Guard selection */}
      {isAdmin ? (
        <div className="mb-6 max-w-md">
          <GuardSelect
            guards={guards}
            selectedGuardId={selectedGuardId}
            onSelect={(id) => setSelectedGuardId(id === 'all' ? undefined : id)}
          />
        </div>
      ) : (
        <div className="mb-6 max-w-sm">
          <Label htmlFor="year">Έτος</Label>
          <YearSelect year={year} onChange={(y) => setYear(y)} />
          <p className="text-sm text-muted-foreground mt-2">Showing your own stats for {year}.</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <StatBox
          title="ΚΑΝΟΝΙΚΗ ΑΔΕΙΑ"
          value={statsQuery.data?.total.regular ?? 0}
          ytdValue={statsQuery.data?.ytd.total.regular ?? 0}
          base={statsQuery.data?.base.regular ?? 0}
          delta={statsQuery.data?.adjustments.regular ?? 0}
          icon={<CalendarX className="w-5 h-5 text-primary" />}
          onClick={() => openDetails('regular')}
        />
        <StatBox
          title="ΚΑΝ ΑΔΕΙΑ ΠΡΟΗΓ ΕΤΟΥΣ"
          value={statsQuery.data?.total.previousYear ?? 0}
          ytdValue={statsQuery.data?.ytd.total.previousYear ?? 0}
          base={statsQuery.data?.base.previousYear ?? 0}
          delta={statsQuery.data?.adjustments.previousYear ?? 0}
          icon={<ClipboardX className="w-5 h-5 text-primary" />}
          onClick={() => openDetails('previousYear')}
        />
        <StatBox
          title="ΑΝΑΡΡΩΤΙΚΗ ΑΔΕΙΑ"
          value={statsQuery.data?.total.sick ?? 0}
          ytdValue={statsQuery.data?.ytd.total.sick ?? 0}
          base={statsQuery.data?.base.sick ?? 0}
          delta={statsQuery.data?.adjustments.sick ?? 0}
          icon={<Stethoscope className="w-5 h-5 text-primary" />}
          onClick={() => openDetails('sick')}
        />
        <StatBox
          title="ΜΗ ΕΡΓΑΣΙΑ"
          value={statsQuery.data?.total.noWork ?? 0}
          ytdValue={statsQuery.data?.ytd.total.noWork ?? 0}
          base={statsQuery.data?.base.noWork ?? 0}
          delta={statsQuery.data?.adjustments.noWork ?? 0}
          icon={<Ban className="w-5 h-5 text-primary" />}
          onClick={() => openDetails('noWork')}
        />
      </div>

      {/* Details dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {selectedType === 'regular' && 'ΚΑΝΟΝΙΚΗ ΑΔΕΙΑ'}
              {selectedType === 'previousYear' && 'ΚΑΝ ΑΔΕΙΑ ΠΡΟΗΓ ΕΤΟΥΣ'}
              {selectedType === 'sick' && 'ΑΝΑΡΡΩΤΙΚΗ ΑΔΕΙΑ'}
              {selectedType === 'noWork' && 'ΜΗ ΕΡΓΑΣΙΑ'}
            </DialogTitle>
            <DialogDescription>
              Ημερομηνίες μέσα στο {year} που αντιστοιχούν στην επιλεγμένη κατηγορία.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-auto">
            {(selectedType && statsQuery.data?.occurrences[selectedType]?.length) ? (
              <ul className="list-disc pl-5">
                {statsQuery.data!.occurrences[selectedType]!.map((o) => (
                  <li key={o.id} className="text-sm text-foreground/90">
                    <span className="font-medium">{o.title}</span>: {format(parseISO(o.start_date), 'dd/MM/yyyy')} – {format(parseISO(o.end_date), 'dd/MM/yyyy')} ({o.days} {o.days === 1 ? 'day' : 'days'})
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">Δεν βρέθηκαν καταγραφές.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin adjustments */}
      {isAdmin && effectiveGuardId && (
        <div className="space-y-4">
          <Separator />
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-semibold">Admin adjustments for {selectedGuardName || 'employee'}</h3>
            <div className="px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full">
              Έτος {year}
            </div>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Οι προσαρμογές αφορούν αποκλειστικά το έτος {year} και δεν επηρεάζουν άλλα έτη.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="regular">ΚΑΝΟΝΙΚΗ ΑΔΕΙΑ (+/-)</Label>
              <Input id="regular" type="number" value={form.regular} onChange={(e) => setForm(v => ({ ...v, regular: Number(e.target.value) }))} />
            </div>
            <div>
              <Label htmlFor="prev">ΚΑΝ ΑΔΕΙΑ ΠΡΟΗΓ ΕΤΟΥΣ (+/-)</Label>
              <Input id="prev" type="number" value={form.previousYear} onChange={(e) => setForm(v => ({ ...v, previousYear: Number(e.target.value) }))} />
            </div>
            <div>
              <Label htmlFor="sick">ΑΝΑΡΡΩΤΙΚΗ ΑΔΕΙΑ (+/-)</Label>
              <Input id="sick" type="number" value={form.sick} onChange={(e) => setForm(v => ({ ...v, sick: Number(e.target.value) }))} />
            </div>
            <div>
              <Label htmlFor="noWork">ΜΗ ΕΡΓΑΣΙΑ (+/-)</Label>
              <Input id="noWork" type="number" value={form.noWork} onChange={(e) => setForm(v => ({ ...v, noWork: Number(e.target.value) }))} />
            </div>
          </div>
          <div>
            <Label htmlFor="notes">Notes</Label>
            <Input id="notes" value={form.notes} onChange={(e) => setForm(v => ({ ...v, notes: e.target.value }))} />
          </div>
          <Button onClick={onSave} disabled={saveMutation.isPending} className="inline-flex items-center gap-2">
            <Save className="w-4 h-4" />
            Save adjustments
          </Button>
        </div>
      )}
    </Card>
  );
};

export default EmployeeNonWorkingStats;
